import Phaser from 'phaser';
import { GAME } from '../constants';
import { FONT_FAMILY, FONT } from './FontLoader';

export { FONT_FAMILY, FONT };

export function pixelTextStyle(
  size: keyof typeof FONT,
  color?: string,
  extra?: Partial<Phaser.Types.GameObjects.Text.TextStyle>,
): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontFamily: FONT_FAMILY,
    fontSize: FONT[size],
    color: color ?? PIXEL_THEME.text,
    ...extra,
  };
}

export const PIXEL_THEME = {
  bg: 0x08090f,
  bgTop: 0x10172a,
  bgBottom: 0x151225,
  frame: 0x5b3340,
  panel: 0x151a2b,
  panelInner: 0x0f1525,
  panelStroke: 0xc06a47,
  panelAccent: 0xffd27c,
  text: '#f2f4ff',
  textMuted: '#9da6c7',
  textAccent: '#ffd166',
  textDanger: '#ff8a72',
  success: '#78d690',
  teal: 0x2f7982,
} as const;

export interface PixelPanelOptions {
  fill?: number;
  stroke?: number;
  accent?: number;
  alpha?: number;
}

export interface PixelPanel {
  bg: Phaser.GameObjects.Rectangle;
  elements: Phaser.GameObjects.Rectangle[];
}

export function drawPixelBackdrop(scene: Phaser.Scene): Phaser.GameObjects.GameObject[] {
  const cx = GAME.WIDTH / 2;
  const cy = GAME.HEIGHT / 2;
  const objects: Phaser.GameObjects.GameObject[] = [];

  const base = scene.add.rectangle(cx, cy, GAME.WIDTH, GAME.HEIGHT, PIXEL_THEME.bg, 1).setDepth(-120);
  const topGlow = scene.add.rectangle(cx, 130, GAME.WIDTH, 300, PIXEL_THEME.bgTop, 0.5).setDepth(-119);
  const bottomGlow = scene.add.rectangle(cx, GAME.HEIGHT - 84, GAME.WIDTH, 220, PIXEL_THEME.bgBottom, 0.55).setDepth(-119);
  const frame = scene.add.rectangle(cx, cy, GAME.WIDTH - 22, GAME.HEIGHT - 18, 0x000000, 0)
    .setStrokeStyle(3, PIXEL_THEME.frame, 0.92)
    .setDepth(-118);
  objects.push(base, topGlow, bottomGlow, frame);

  for (let y = 22; y < GAME.HEIGHT - 20; y += 8) {
    const line = scene.add.rectangle(cx, y, GAME.WIDTH - 28, 1, 0x000000, 0.08).setDepth(-117);
    objects.push(line);
  }

  for (let i = 0; i < 56; i++) {
    const x = 24 + ((i * 97) % (GAME.WIDTH - 48));
    const y = 20 + ((i * 61) % (GAME.HEIGHT - 40));
    const c = i % 7 === 0 ? PIXEL_THEME.panelAccent : 0x41527c;
    const dot = scene.add.rectangle(x, y, 2, 2, c, i % 7 === 0 ? 0.2 : 0.08).setDepth(-115);
    objects.push(dot);

    // Slow drift animation
    scene.tweens.add({
      targets: dot,
      x: x + ((i % 3) - 1) * 18,
      y: y + ((i % 5) - 2) * 12,
      duration: 8000 + (i * 137) % 7000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  return objects;
}

export function createPixelPanel(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  options: PixelPanelOptions = {},
): PixelPanel {
  const fill = options.fill ?? PIXEL_THEME.panel;
  const stroke = options.stroke ?? PIXEL_THEME.panelStroke;
  const accent = options.accent ?? PIXEL_THEME.panelAccent;
  const alpha = options.alpha ?? 0.9;

  const shadow = scene.add.rectangle(x + 3, y + 3, width, height, 0x000000, 0.42);
  const bg = scene.add.rectangle(x, y, width, height, fill, alpha).setStrokeStyle(2, stroke, 1);
  const inner = scene.add.rectangle(x, y, width - 8, height - 8, PIXEL_THEME.panelInner, 0.4)
    .setStrokeStyle(1, stroke, 0.45);
  const accentTop = scene.add.rectangle(x, y - height / 2 + 5, width - 10, 3, accent, 0.55);
  const c1 = scene.add.rectangle(x - width / 2 + 5, y - height / 2 + 5, 2, 2, accent, 0.72);
  const c2 = scene.add.rectangle(x + width / 2 - 5, y - height / 2 + 5, 2, 2, accent, 0.72);
  const c3 = scene.add.rectangle(x - width / 2 + 5, y + height / 2 - 5, 2, 2, accent, 0.72);
  const c4 = scene.add.rectangle(x + width / 2 - 5, y + height / 2 - 5, 2, 2, accent, 0.72);

  return {
    bg,
    elements: [shadow, bg, inner, accentTop, c1, c2, c3, c4],
  };
}
